import GameConfig from "../core/data/GameConfig";
import ResizableScene from "../core/ResizableScene";
import { CameraController } from "../game/CameraController";
import { ChunkLoader } from "../game/ChunkLoader";
import CloudSpawner from "../game/CloudSpawner";
import { DecoSpawner } from "../game/DecoSpawner";
import { Flag } from "../game/Flag";
import { GameObject } from "../game/GameObject";
import { GameTutorial } from "../game/GameTutorial";
import { GameUI } from "../game/GameUI";
import { Layer, Layers } from "../game/Layers";
import { Player } from "../game/Player";
import { SnowParticles } from "../game/SnowParticles";
import PlayerData from "../PlayerData";
import { LevelConfig } from "../types/LevelConfig";
import { CertificatePopup } from "../ui/CertificatePopup";
import { ConfirmQuitPopup } from "../ui/ConfirmQuitPopup";
import { GameCompletePopup } from "../ui/GameCompletePopup";
import { HelpPopup } from "../ui/HelpPopup";
import { PausePopup } from "../ui/PausePopup";
import { RecapPopup } from "../ui/RecapPopup";
import { TutorialPopup } from "../ui/TutorialPopup";
import ARIA from "../utils/ARIA";
import ArrayUtils from "../utils/ArrayUtils";
import { LevelData } from "./LevelSelectScene";

export default class GameScene extends ResizableScene {
	private _onResize: () => any;

	bgConatiner: Phaser.GameObjects.Container;
	gameContainer: Phaser.GameObjects.Container;
	uiContainer: Phaser.GameObjects.Container;
	ratioContainer: Phaser.GameObjects.Container;
	hudContainer: Phaser.GameObjects.Container;
	topContainer: Phaser.GameObjects.Container;
	layers: Layers;

	ambientAnims: Phaser.GameObjects.GameObject[] = [];
	objects: GameObject[];
	camController: CameraController;
	chunkLoader: ChunkLoader;
	decoSpawner: DecoSpawner;
	player: Player;
	ui: GameUI;
	pausePopup: PausePopup;
	score: number;
	level: LevelData;

	recapPopup: RecapPopup;
	gameCompletePopup: GameCompletePopup;
	tutorial: GameTutorial;

	readonly upperLaneY = 550;
	readonly lowerLaneY = 800;
	public angle = 14;
	snowLayer: Phaser.GameObjects.Container;
	snowParticles: Phaser.GameObjects.Particles.ParticleEmitter[];

	currentSoundVolume: number;
	tutorialChunkCount: number;
	progressTweenFlag: boolean;
	alignToViewport: boolean;
	init(data: { level: LevelData }): void {
		this.level = data.level;
		if (this.level == null) {
			this.level = this.cache.json.get("levels")[0];
		}
		super.init();
	}

	create(): void {
		super.create();
		this.music.play("gameMusic");
		this.soundManager.play("ambienceSFX", false, { loop: true });
		this.time.paused = false;

		this.tutorialChunkCount = 0;
		this.progressTweenFlag = true;
		this.objects = [];
		// bg conatiner for static background objects
		this.bgConatiner = this.add.container(0, 0);
		this.gameContainer = this.add.container(0, 0);
		this.uiContainer = this.add.container(0, 0);
		this.ratioContainer = this.add.container(0, 0);

		this.ratioContainer.setSize(1920, 1080);
		this.alignToViewport = true;

		this.hudContainer = this.add.container(0, 0);
		this.topContainer = this.add.container(0, 0);
		this.uiContainer.add([this.hudContainer, this.topContainer]);

		const bgCamera = this.cameras.add();
		bgCamera.setName("bg");
		bgCamera.ignore(this.uiContainer);
		bgCamera.ignore(this.gameContainer);
		this.cameras.main.cameraManager.remove(bgCamera, false);
		this.cameras.main.cameraManager.cameras.unshift(bgCamera);

		const uiCamera = this.cameras.add();
		uiCamera.setName("ui");
		this.cameras.main.ignore(this.uiContainer);
		this.cameras.main.ignore(this.bgConatiner);
		uiCamera.ignore(this.gameContainer);
		uiCamera.ignore(this.bgConatiner);

		this.ambientAnims = [];

		const sky = this.add.image(0, 0, "sky");
		this.bgConatiner.add(sky);

		const clouds = new CloudSpawner(this, {
			images: ["cloud1", "cloud2"],
			spawnTime: { min: 4, max: 8 },
			speed: { min: -50, max: -70 },
			scale: { min: 0.2, max: 0.6 },
			maxClouds: 10,
			minY: 40,
			maxY: 200,
		});
		this.bgConatiner.add(clouds);
		this.ambientAnims.push(clouds);

		// Spawn Clouds so the sky isn't completely empty at the start
		const width = this.viewport.width;
		clouds.spwanCloud(-width * Phaser.Math.RND.realInRange(0.65, 0.9));
		clouds.spwanCloud(-width * Phaser.Math.RND.realInRange(0.35, 0.6));
		clouds.spwanCloud(-width * Phaser.Math.RND.realInRange(0.1, 0.3));

		const mountain = this.add.image(0, 0, "mountain");
		this.bgConatiner.add(mountain);

		this.layers = new Layers(this);
		this.layers.addLayers(this.gameContainer, "bg", "game", "foreground");

		const levelConfigs = GameConfig.get("levels") as LevelConfig[];
		const config = levelConfigs.find((c) => c.id == this.level.name) as LevelConfig;

		const camController = (this.camController = new CameraController(this.cameras.main));
		if (config.camAngle) {
			this.angle = config.camAngle;
		}
		this.cameras.main.setAngle(this.angle);

		const inputObj = this.add.image(0, 0, "whiteSquare");
		this.hudContainer.add(inputObj);
		inputObj.alpha = 0.0001;
		inputObj.setSize(4000, 4000);
		inputObj.setInteractive();

		const player = (this.player = new Player(this, this.upperLaneY + 20, this.lowerLaneY + 20, inputObj));
		this.addObject(player);
		player.movement.enableMovement = true;
		if (config.playerAngle) {
			player.angle = config.playerAngle;
		}

		let configCamYOffset = 0;
		if (config.camYOffset) {
			configCamYOffset = config.camYOffset;
		}
		camController.startFollow(player, 500, -50 + configCamYOffset);
		camController.centerOn(player);

		this.ui = new GameUI(this, this.hudContainer, this.topContainer, this.level.progressBarMarkers);

		const widescreenBorder1 = this.add.image(0, 0, "widescreenBorder");
		const widescreenBorder2 = this.add.image(0, 0, "widescreenBorder");
		widescreenBorder1.setOrigin(0.5, 0.5);
		widescreenBorder2.setOrigin(0.5, 0.5);
		widescreenBorder1.angle += 90;
		widescreenBorder2.angle -= 90;
		this.hudContainer.add(widescreenBorder1);
		this.hudContainer.add(widescreenBorder2);

		this.chunkLoader = new ChunkLoader(this, this.level);
		this.decoSpawner = new DecoSpawner(this);
		this.score = 0;

		player.once(Player.ON_LAUNCH, this.chunkLoader.onPlayerLaunched, this.chunkLoader);

		this._onResize = (): void => {
			if (this.ratioContainer.width > this.viewport.width) {
				this.ratioContainer.scale = this.viewport.width / this.ratioContainer.width;
				this.alignToViewport = true;
			} else this.alignToViewport = false;

			this.ratioContainer.scale = 1;
			this.alignIn.Center(this.ratioContainer, this.viewport);

			sky.setScale(this.ratioContainer.width / sky.width);
			this.alignIn.TopCenter(sky, this.ratioContainer);

			mountain.setScale(this.ratioContainer.width / mountain.width);
			this.alignIn.BottomCenter(mountain, this.ratioContainer);
			this.ui.resize();

			const borderScale = this.viewport.height / widescreenBorder1.width;
			widescreenBorder1.scale = borderScale;
			widescreenBorder2.scale = borderScale;

			this.alignIn.LeftCenter(widescreenBorder1, this.ratioContainer, widescreenBorder1.height + 810);
			this.alignIn.RightCenter(widescreenBorder2, this.ratioContainer, widescreenBorder2.height + 810);
		};
		this.resize();

		this.snowLayer = this.add.container(0, 0);
		this.snowLayer.setSize(this.viewport.width, this.viewport.height);
		this.snowParticles = SnowParticles.create(this, this.snowLayer);

		this.physics.world.gravity.set(0, 0);
		this.physics.world.on(Phaser.Physics.Arcade.Events.WORLD_STEP, this.physicsUpdate, this);

		// this.input.keyboard.on("keydown-P", (): void => {
		// 	this.score = 99999;
		// 	this.onFinish();
		// 	// const pop = new GameCompletePopup(
		// 	// 	this,
		// 	// 	() => {
		// 	// 		this.createCertificatePopup();
		// 	// 	},
		// 	// 	this
		// 	// );
		// 	// this.uiContainer.add(pop);
		// });
		if (PlayerData.Instance.saveData.firstTimeUser || PlayerData.Instance.saveData.highScores[this.level.name] == undefined) {
			if (this.level.hasTutorial) {
				this.chunkLoader.tutorial = true;
				this.player.movement.enableMovement = false;
				this.createTutorialPopup();
				PlayerData.Instance.saveData.firstTimeUser = false;
				PlayerData.Instance.save();
			} else {
				this.chunkLoader.tutorial = false;
				this.ui.progressGroup.setVisible(true);
				return;
			}
		} else this.ui.progressGroup.setVisible(true);
	}

	shutdown(): void {
		this.tweens.killAll();
		this.anims.removeAllListeners();
		this._onResize = null;
		this.objects = [];
		this.bgConatiner = null;
		this.gameContainer = null;
		this.uiContainer = null;
		this.hudContainer = null;
		this.topContainer = null;
		this.layers = null;
		this.camController = null;
		this.ui = null;
		this.pausePopup = null;
		this.ambientAnims = [];

		if (this.pausePopup) this.pausePopup.destroy();
		this.pausePopup = null;
		if (this.physics.world) this.physics.world.off(Phaser.Physics.Arcade.Events.WORLD_STEP, this.physicsUpdate, this);

		this.soundManager.stop("rampSFX");
		this.soundManager.stop("ambienceSFX");
	}

	pausePhaserSystems(): void {
		this.physics.world.pause();
		this.currentSoundVolume = this.soundManager.volume;
		this.soundManager.stop("ambienceSFX");
		for (const obj of this.objects) {
			if (obj instanceof Flag) {
				obj.onPause();
			}
		}
		this.ui.onPause();
		this.player.onPause();
		SnowParticles.pauseEmitters(this.snowParticles);
		this.time.paused = true;
		this.tweens.pauseAll();
		this.anims.pauseAll();
	}

	resumePhaserSystems(): void {
		this.physics.world.resume();
		this.time.paused = false;
		this.player.onResume();
		this.ui.onResume();
		for (const obj of this.objects) {
			if (obj instanceof Flag) {
				obj.onResume();
			}
		}
		this.soundManager.play("ambienceSFX", false, { loop: true });
		SnowParticles.resumeEmitters(this.snowParticles);
		this.tweens.resumeAll();
		this.anims.resumeAll();
	}

	update(t: number, dt: number): void {
		super.update(t, dt);
		if (this.time.paused) return;
		dt /= 1000;
		this.physics.world.step(dt);
		for (const obj of this.objects) obj.update(dt);

		this.layers.get("game").sort("y");
		this.ui.update(dt);

		this.camController.update(dt);
		this.chunkLoader.update();
		this.decoSpawner.update();

		for (const obj of this.objects) {
			if (obj.x + obj.width < this.camController.x - 400) obj.destroy();
		}

		for (const anim of this.ambientAnims) anim.update(dt);

		const chunkWidth = 20; // might not be consistent if chunk sizes are changed in tiled
		const chunkSize = this.chunkLoader.scaleRatio * 100 * chunkWidth;
		const levelStart = this.player.startX + this.tutorialChunkCount * chunkSize;
		const levelEnd = this.chunkLoader.startX + chunkSize * this.level.length + this.tutorialChunkCount * chunkSize;
		let progress = (this.player.x - levelStart) / (levelEnd - levelStart);
		progress = Phaser.Math.Clamp(progress, 0, 1);
		//console.log("Progress: " + progress);

		this.ui.setProgress(progress);
		// if (progress > 0 && this.progressTweenFlag) {
		if (this.progressTweenFlag) {
			this.add.tween({
				targets: this.ui.progressGroup,
				y: { from: this.ui.progressGroup.y + 600, to: this.ui.progressGroup.y },
				duration: 500,
				ease: Phaser.Math.Easing.Quadratic.Out,
			});
			this.progressTweenFlag = false;
			this.ui.progressGroup.y += 600;
			this.ui.progressGroup.setVisible(true);
		}
		//}
	}

	physicsUpdate(dt: number): void {
		if (this.time.paused) return;
		for (const obj of this.objects) {
			if ((<any>obj).physicsUpdate) (<any>obj).physicsUpdate(dt);
		}

		const physicsObjects = this.objects.filter((obj) => obj.body != null);
		this.physics.overlap(physicsObjects, null, (a: any, b: any) => {
			if (a.onOverlap) a.onOverlap(a, b);
			if (b.onOverlap) b.onOverlap(b, a);
		});
	}

	pauseGame(): void {
		if (this.pausePopup) return;
		this.time.paused = true;
		this.pausePhaserSystems();
		this.objects.forEach((obj) => obj.onPause());
		this.pausePopup = new PausePopup(
			this,
			() => {
				this.resumeGame();
				this.pausePopup.destroy();
				this.pausePopup = null;
			},
			this,
			() => {
				this.pausePopup.setVisible(false);
				this.createHelpPopup();
			},
			this,
			() => {
				this.pausePopup.setVisible(false);
				this.createQuitPopup();
			},
			this
		);
		this.uiContainer.add(this.pausePopup);
	}

	resumeGame(): void {
		this.time.paused = false;
		this.objects.forEach((obj) => obj.onResume());
		this.resumePhaserSystems();
		// this.ui.progressGroup.setVisible(true);
	}

	createTutorialPopup(): void {
		this.time.delayedCall(100, () => {
			this.time.paused = true;
			this.pausePhaserSystems();
			this.objects.forEach((obj) => obj.onPause());
			let start = 1;
			let end = 3;
			if (this.level.name == "BlueSquare") {
				start = 4;
				end = 5;
			}
			const tutorialPopup = new TutorialPopup(
				this,
				() => {
					if (this.pausePopup) this.pausePopup.destroy();
					this.pausePopup = null;
					this.tutorial = new GameTutorial(this, this.level.name);
					this.resumeGame();
					this.player.movement.enableMovement = true;
				},
				start,
				end
			);
			this.uiContainer.add(tutorialPopup);
		});
	}

	createHelpPopup(): void {
		const helpPopup = new HelpPopup(this, () => {
			this.pausePopup.setVisible(true);
			ARIA.showLayer("pause-popup", true);
		});
		this.uiContainer.add(helpPopup);
	}

	createQuitPopup(): void {
		const quitPopup = new ConfirmQuitPopup(
			this,
			() => {
				this.soundManager.stop("ambienceSFX");
			},
			this,
			() => {
				quitPopup.destroy();
				this.pausePopup.setVisible(true);
				ARIA.showLayer("pause-popup", true);
			},
			this
		);
		this.uiContainer.add(quitPopup);
	}

	createGameCompletePopup(): void {
		PlayerData.Instance.saveData.certificateSeen = true;
		const pop = (this.gameCompletePopup = new GameCompletePopup(
			this,
			() => {
				if (this.recapPopup) this.recapPopup.destroy();
				this.createCertificatePopup();
			},
			this
		));
		this.uiContainer.add(pop);
	}

	createCertificatePopup(): void {
		const pop = new CertificatePopup(this);
		if (this.gameCompletePopup) this.gameCompletePopup.destroy();
		this.uiContainer.add(pop);
	}

	addObject(obj: GameObject, layerName: Layer = "game"): void {
		this.objects.push(obj);
		this.add.existing(obj);
		const layer = this.layers.get(layerName);
		layer.add(obj);
		obj.once(Phaser.GameObjects.Events.DESTROY, this.removeObject, obj);
	}

	removeObject(obj: GameObject): void {
		if (this.objects) ArrayUtils.remove(this.objects, obj);
	}

	removeAllObjects(): void {
		for (let i = 0; i < this.objects.length; i++) {
			ArrayUtils.remove(this.objects, this.objects[i]);
		}
	}

	resize(): void {
		super.resize();
		if (this._onResize != null) this._onResize();
	}

	addScore(amt: number, lostScore = false): void {
		this.score += amt;
		if (this.score < 0) this.score = 0;
		if (lostScore) this.ui.tweenScore();
		this.ui.setScore(this.score);
	}

	endTutorial(): void {
		this.tutorial = null;
		// this.progressTweenFlag = true;
	}

	onFinish(): void {
		this.player.gainPoints(GameConfig.get("scoreFinishLine"), false);
		this.add.tween({
			targets: this.ui.progressGroup,
			y: { from: this.ui.progressGroup.y, to: this.ui.progressGroup.y + 600 },
			duration: 500,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});
		this.time.delayedCall(1500, (): void => {
			PlayerData.Instance.saveData.totalScore += this.score;
			PlayerData.Instance.save();
			const popup = (this.recapPopup = new RecapPopup(this, this.level, this.score));
			this.uiContainer.add(popup);
		});
	}
}
